import { CustomFieldStudentAccess, StudentProfileFieldAccess } from '@/types/composition-functions';
import type { CustomFieldFieldsFragment, StudentProfileFieldSettingsQuery } from '@/types/operations';
import { CUSTOM_FIELD_TARGET_PREFIX, customFieldKindLabel, customFieldTypeLabel } from '@/utils/customFields';
import { STUDENT_ACCESS_OPTIONS, studentAccessNote } from '@/utils/studentProfileForm';

/**
 * Settings → Custom Fields → Students: the built-in and custom student fields
 * as one list with one "Students" setting each, and the groups the data
 * request wizard picks from. Kept out of the components so it can be
 * unit-tested.
 */

export type BuiltInFieldSetting = StudentProfileFieldSettingsQuery['studentProfileFieldSettings'][number];

export interface StudentFieldRow {
  /** The key a data request asks for: "custom:<key>", or the built-in column. */
  key: string;
  label: string;
  /** The custom field; null for a built-in field. */
  field: CustomFieldFieldsFragment | null;
  /** "Dropdown (Nigerian states)", "Long text", … */
  kind: string;
  access: CustomFieldStudentAccess;
  required: boolean;
  active: boolean;
  valuesCount: number;
  /** Why "Can fill in" still leaves the field read-only for students, if it does. */
  note: string | null;
}

/** The key a data request uses for a custom field. */
export function dataRequestKey(field: Pick<CustomFieldFieldsFragment, 'key'>): string {
  return `${CUSTOM_FIELD_TARGET_PREFIX}${field.key}`;
}

/** A built-in field's setting as the shared "Students" choice. */
export function builtInStudentAccess(access: StudentProfileFieldAccess): CustomFieldStudentAccess {
  return access === StudentProfileFieldAccess.Off ? CustomFieldStudentAccess.Hidden : CustomFieldStudentAccess.Edit;
}

/** The built-in setting for a "Students" choice and a Required tick. */
export function builtInSettingFor(access: CustomFieldStudentAccess, required: boolean): StudentProfileFieldAccess {
  if (access !== CustomFieldStudentAccess.Edit) return StudentProfileFieldAccess.Off;
  return required ? StudentProfileFieldAccess.Required : StudentProfileFieldAccess.Optional;
}

/** Custom fields in the school's order, then the built-in fields. */
export function studentFieldRows(
  custom: ReadonlyArray<CustomFieldFieldsFragment>,
  builtIn: ReadonlyArray<BuiltInFieldSetting>,
): StudentFieldRow[] {
  return [
    ...custom.map(field => ({
      key: dataRequestKey(field),
      label: field.label,
      field,
      kind: customFieldKindLabel(field, custom),
      access: field.student_access,
      required: field.is_required,
      active: field.is_active,
      valuesCount: field.values_count,
      note: studentAccessNote(field, custom),
    })),
    ...builtIn.map(setting => ({
      key: setting.key,
      label: setting.label,
      field: null,
      kind: customFieldTypeLabel(setting.type),
      access: builtInStudentAccess(setting.access),
      required: setting.access === StudentProfileFieldAccess.Required,
      active: true,
      valuesCount: setting.values_count,
      note: null,
    })),
  ];
}

/**
 * The "Students" choices on a row. Students cannot be shown a built-in
 * field read-only: it is staff only or theirs to fill in.
 */
export function studentAccessChoices(row: Pick<StudentFieldRow, 'field'>): Array<{ label: string; value: CustomFieldStudentAccess }> {
  return STUDENT_ACCESS_OPTIONS
    .filter(o => row.field || o.value !== CustomFieldStudentAccess.View)
    .map(o => ({ label: o.short, value: o.value }));
}

/**
 * Required only means something to students once they fill the field in, so
 * a built-in field that is staff only cannot be ticked. A custom field's
 * Required also applies to staff, so it can always change.
 */
export function canToggleRequired(row: Pick<StudentFieldRow, 'field' | 'access' | 'active'>): boolean {
  if (!row.active) return false;
  return !!row.field || row.access === CustomFieldStudentAccess.Edit;
}

/**
 * The fields "Let students fill this in" changes to "Can fill in" for this
 * one: a state and the LGA that follows it change together, so opening
 * either opens the other when students could not fill it in otherwise.
 * Empty when nothing it may change would open the field (its state is
 * retired, say): the row's note says why.
 */
export function fieldsToOpen(row: StudentFieldRow, rows: ReadonlyArray<StudentFieldRow>): StudentFieldRow[] {
  const linked = new Map<string, StudentFieldRow>([[row.key, row]]);
  const queue = row.field ? [row] : [];
  while (queue.length) {
    const current = queue.shift()!.field!;
    const related = rows.filter(r => r.field && r.active && !linked.has(r.key) && (
      // A follower students cannot fill in keeps its state read-only.
      (r.field.depends_on === current.key && r.access !== CustomFieldStudentAccess.Edit)
      // A state students cannot see keeps its follower read-only.
      || (current.depends_on === r.field.key && r.access === CustomFieldStudentAccess.Hidden)
    ));
    for (const r of related) {
      linked.set(r.key, r);
      queue.push(r);
    }
  }
  return [...linked.values()].filter(r => r.access !== CustomFieldStudentAccess.Edit);
}

export interface DataRequestPickerGroups {
  /** What a request may ask for now: the server's list, with each field's kind. */
  open: Array<{ key: string; label: string; kind: string }>;
  /** Active fields students cannot fill in yet. */
  closed: StudentFieldRow[];
}

/**
 * The wizard's field list in two groups. The server decides what is open
 * (an LGA under a hidden state is not, even when set to "Can fill in").
 */
export function dataRequestPickerGroups(
  choices: ReadonlyArray<{ key: string; label: string }>,
  rows: ReadonlyArray<StudentFieldRow>,
): DataRequestPickerGroups {
  const fillable = new Set(choices.map(c => c.key));
  const kinds = new Map(rows.map(r => [r.key, r.kind]));
  return {
    open: choices.map(c => ({ key: c.key, label: c.label, kind: kinds.get(c.key) ?? '' })),
    closed: rows.filter(r => r.active && !fillable.has(r.key)),
  };
}

/** "Students can now fill in State of Origin and Local Government Area." */
export function openedFieldsMessage(rows: ReadonlyArray<Pick<StudentFieldRow, 'label'>>): string {
  const labels = rows.map(r => r.label);
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0] ?? '';
  return `Students can now fill in ${list}.`;
}
